/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-plusplus */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactFlow, { Controls, Background, BackgroundVariant, ReactFlowProvider, Node, Edge } from 'reactflow';
import { useSelector, useDispatch } from 'react-redux';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { clearAllNodeConfigurations, clearSelectedTrigger } from 'src/redux/workflows/slice';
import 'reactflow/dist/style.css';
import { Container } from 'src/components/layouts/Container/Container';
import NodePanel from 'src/components/nodes/NodePanel/NodePanel';
import { workflowEdgeTypes, workflowNodeTypes } from 'src/components/nodes/nodeTypes';
import useWorkflows from 'src/hooks/useWorkflows';
import SaveWorkflowModal from 'src/components/modals/SaveWorkflowModal/SaveWorkflowModal';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { useCreateWorkflow, WorkflowCreateInput, WorkflowItemModel } from 'src/services/workflows';
import { WorkflowValidationContext } from 'src/components/nodes/WorkflowValidationContext';
import { validateWorkflow } from 'src/utils/workflowValidation';
import { RECORD_CONTACT_RECIPIENT } from 'src/utils/workflowRecipients';
import { formatConditionValue, formatActionTypeKey, mapFieldTypeToValueType } from 'src/utils/general';
import './styles/Workflows.scss';
import { ButtonToneProps } from 'src/components/ui/Button/Button';

export interface WorkflowPrefill {
  root: WorkflowItemModel;
  name?: string;
  description?: string;
  oneTime?: boolean;
  triggerName?: string;
}

function WorkflowCreate() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workflowsState = useSelector(getWorkflows);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showIssues, setShowIssues] = useState(false);

  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    panelOptions,
    calculateLeftPosition,
    calculateTopPostion,
    loadFromTree,
  } = useWorkflows(workflowsState);
  const location = useLocation();
  const prefill = (location.state as WorkflowPrefill | null) ?? null;
  const prefillApplied = useRef(false);
  const [oneTime, setOneTime] = useState(prefill?.oneTime ?? true);
  const displayNodes = useMemo(
    () => nodes.map(node => (node.type === 'trigger' ? { ...node, data: { ...node.data, oneTime } } : node)),
    [nodes, oneTime],
  );

  useEffect(() => {
    dispatch(clearAllNodeConfigurations());
    dispatch(clearSelectedTrigger());
  }, [dispatch]);

  const createWorkflowMutation = useCreateWorkflow();

  useEffect(() => {
    if (!prefill?.root || prefillApplied.current) return;
    prefillApplied.current = true;
    dispatch(clearAllNodeConfigurations());
    dispatch(clearSelectedTrigger());
    loadFromTree(prefill.root, prefill.triggerName);
  }, [prefill, loadFromTree, dispatch]);

  const hasUnsavedChanges = useMemo(() => {
    return workflowsState.selectedTrigger !== null || Object.keys(workflowsState.nodeConfigurations).length > 0;
  }, [workflowsState.selectedTrigger, workflowsState.nodeConfigurations]);

  const nodeTypes = workflowNodeTypes;
  const edgeTypes = workflowEdgeTypes;

  const transformWorkflowData = (
    title: string,
    description: string,
    nodes: Node[],
    edges: Edge[],
    oneTime: boolean,
  ): WorkflowCreateInput => {
    const triggerNode = nodes.find(node => node.type === 'trigger');

    if (!triggerNode) {
      throw new Error('No trigger node found in workflow');
    }

    const findNextWorkflowNode = (nodeId: string): string | null => {
      const outgoingEdges = edges.filter(edge => edge.source === nodeId);

      if (outgoingEdges.length === 0) {
        return null;
      }

      const edge = outgoingEdges[0];
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!targetNode) {
        return null;
      }

      if (targetNode.type === 'plus') {
        return findNextWorkflowNode(edge.target);
      }
      return edge.target;
    };

    const buildWorkflowItem = (nodeId: string): any => {
      const node = nodes.find(n => n.id === nodeId);
      if (!node || node.type === 'plus') return null;

      const nodeConfig = workflowsState.nodeConfigurations[nodeId];
      let relatedObject: any;

      switch (node.type) {
        case 'trigger':
          if (!workflowsState.selectedTrigger) {
            throw new Error('No trigger selected');
          }
          relatedObject = {
            id: workflowsState.selectedTrigger.id,
          };
          break;

        case 'condition': {
          if (!nodeConfig || nodeConfig.nodeType !== 'condition') {
            throw new Error(`No configuration found for condition node ${nodeId}`);
          }
          const conditionData = nodeConfig.data as any;
          const conditions = conditionData.conditions || [];
          if (!conditions.length) {
            throw new Error(`Condition node ${nodeId} has no conditions`);
          }
          // The BE expects a condition group (operator + condition_ids), not a
          // flat condition.
          relatedObject = {
            operator: conditionData.operator || 'and',
            condition_ids: conditions.map((c: any) => ({
              attribute: c.attribute,
              condition: formatConditionValue(c.condition),
              value: c.value,
              value_type: mapFieldTypeToValueType(c.valueType, c.value),
            })),
            subgroup_ids: [],
          };
          break;
        }

        case 'emailBuilder':
        case 'notificationBuilder': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          const actionData = nodeConfig.data as any;
          relatedObject = {
            action_type: formatActionTypeKey(actionData.actionType || node.type),
            title: actionData.title || '',
            return_value: actionData.returnValue || actionData.content || actionData.message || '',
          };
          break;
        }

        case 'productBuilder': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          const actionData = nodeConfig.data as any;

          const detailValues: any = {};
          if (actionData.unit) {
            detailValues.unit = actionData.unit;
          }
          if (actionData.amount) {
            detailValues.amount = actionData.amount;
          }

          relatedObject = {
            action_type: 'product',
            return_value: actionData.selectedProductId ? actionData.selectedProductId.toString() : '',
            ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
          };
          break;
        }

        case 'caseBuilder': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          const actionData = nodeConfig.data as any;

          const detailValues: any = {};
          if (actionData.sectionId) {
            detailValues.section = actionData.sectionId;
          }

          relatedObject = {
            action_type: 'case',
            title: actionData.title || '',
            return_value: actionData.message || '',
            ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
          };
          break;
        }

        case 'action': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          const actionData = nodeConfig.data as any;
          const actionTypeKey = formatActionTypeKey(actionData.actionType || 'action');

          if (actionTypeKey === 'product') {
            const detailValues: any = {};
            if (actionData.unit) {
              detailValues.unit = actionData.unit;
            }
            if (actionData.amount) {
              detailValues.amount = actionData.amount;
            }
            const productId =
              actionData.selectedProductId !== undefined && actionData.selectedProductId !== null
                ? String(actionData.selectedProductId)
                : actionData.returnValue || '';
            if (!productId) {
              throw new Error('Product action is missing a selected product');
            }
            relatedObject = {
              action_type: 'product',
              return_value: productId,
              ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
            };
          } else {
            const detailValues: any = {};
            if (actionTypeKey === 'notification' && actionData.partnerId) {
              detailValues.recipient_user_id = actionData.partnerId;
              if (actionData.partnerName) detailValues.recipient_user_name = actionData.partnerName;
            }
            if (actionTypeKey === 'mail' && actionData.recipient && actionData.recipient !== RECORD_CONTACT_RECIPIENT) {
              detailValues.recipient_email = actionData.recipient;
            }
            relatedObject = {
              action_type: actionTypeKey,
              title: actionData.title || '',
              return_value: actionData.returnValue || actionData.content || actionData.message || '',
              ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
            };
          }
          break;
        }

        default:
          throw new Error(`Unknown node type: ${node.type}`);
      }

      return {
        item_type: node.type === 'trigger' ? 'trigger' : node.type === 'condition' ? 'condition' : 'action',
        related_object: relatedObject,
      };
    };

    const buildWorkflowTree = (nodeId: string): any => {
      const currentItem = buildWorkflowItem(nodeId);
      if (!currentItem) return null;

      const nextNodeId = findNextWorkflowNode(nodeId);

      if (nextNodeId) {
        const nextItem = buildWorkflowTree(nextNodeId);
        if (nextItem) {
          currentItem.true_result = nextItem;
        }
      }

      return currentItem;
    };

    const root = buildWorkflowTree(triggerNode.id);
    if (!root) {
      throw new Error('Failed to build workflow tree');
    }

    return {
      name: title,
      description: description || null,
      one_time: oneTime,
      root,
    };
  };

  const issues = useMemo(
    () => validateWorkflow(nodes, workflowsState.nodeConfigurations, !!workflowsState.selectedTrigger, t),
    [nodes, workflowsState.nodeConfigurations, workflowsState.selectedTrigger, t],
  );

  const validationValue = useMemo(() => ({ issues, showIssues }), [issues, showIssues]);

  const ensureValid = () => {
    if (!issues.length) return true;
    setShowIssues(true);
    toast.error(t('workflows.validation.fix_before_save'));
    return false;
  };

  const handleCreateWorkflow = async (title: string, description: string, oneTime: boolean) => {
    if (!ensureValid()) return;

    try {
      setIsCreating(true);

      const workflowData = transformWorkflowData(title, description, nodes, edges, oneTime);

      await createWorkflowMutation.mutateAsync({
        body: workflowData,
      });

      toast.success(t('success.workflow_created'));
      setIsModalOpen(false);
      dispatch(clearAllNodeConfigurations());
      dispatch(clearSelectedTrigger());
      navigate('/workflows');
    } catch (error) {
      console.error('Error creating workflow:', error);
      toast.error(t('errors.workflow_creation_failed'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setIsCancelConfirmationOpen(true);
    } else {
      dispatch(clearAllNodeConfigurations());
      dispatch(clearSelectedTrigger());
      navigate('/workflows');
    }
  };

  const handleConfirmCancel = (confirmed: boolean) => {
    setIsCancelConfirmationOpen(false);
    if (confirmed) {
      dispatch(clearAllNodeConfigurations());
      dispatch(clearSelectedTrigger());
      navigate('/workflows');
    }
  };

  const buttons = [
    {
      title: t('general.cancel'),
      tone: ButtonToneProps.SECONDARY,
      onClick: handleCancelClick,
    },
    {
      title: t('general.save'),
      tone: ButtonToneProps.PRIMARY_BLUE,
      onClick: () => {
        if (ensureValid()) setIsModalOpen(true);
      },
    },
  ];

  return (
    <>
      <Container title={`Workflows / ${t('workflows.new_workflow')}`} buttons={buttons}>
        <WorkflowValidationContext.Provider value={validationValue}>
          {showIssues && issues.length > 0 && (
            <div className="mb-16 rounded-base status-error-surface status-error-full px-16 py-12 flex flex-col gap-y-4">
              <span className="typo-text-14--bold">{t('workflows.validation.title')}</span>
              {issues.map(issue => (
                <span key={`${issue.nodeId}-${issue.message}`} className="typo-text-12">
                  {issue.message}
                </span>
              ))}
            </div>
          )}
          <div style={{ width: '100%', height: 'calc(100dvh - 6.4rem)' }}>
            <ReactFlow
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              panOnDrag
              nodes={displayNodes}
              onNodesChange={onNodesChange}
              edges={edges}
              onEdgesChange={onEdgesChange}
              proOptions={{ hideAttribution: true }}>
              <Controls />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>

            {workflowsState.showNodesType && (
              <div
                style={{
                  position: 'absolute',
                  left: calculateLeftPosition,
                  top: calculateTopPostion,
                }}>
                <NodePanel data={panelOptions} />
              </div>
            )}
          </div>
        </WorkflowValidationContext.Provider>
      </Container>

      <SaveWorkflowModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleCreateWorkflow}
        isLoading={isCreating}
        initialTitle={prefill?.name ?? ''}
        initialDescription={prefill?.description ?? ''}
        initialOneTime={oneTime}
        setOneTime={setOneTime}
      />

      <ConfirmationModal
        isOpen={isCancelConfirmationOpen}
        title={t('workflows.cancel_changes')}
        description={t('workflows.cancel_changes_confirmation')}
        onClose={handleConfirmCancel}
      />
    </>
  );
}

function WorkflowsWithProvider() {
  return (
    <ReactFlowProvider>
      <WorkflowCreate />
    </ReactFlowProvider>
  );
}

export default WorkflowsWithProvider;
